import {
  type DocumentViewerMessage,
  DocumentViewport,
  Layer,
  Page,
  PDFPageSurface,
  selViewerPageTransform
} from '@st/document-viewer-kit'
import { type ReactNode, useMemo } from 'react'
import { match } from '@st/pattern'
import type { STDocViewerRuntimeState } from '~/st-doc-viewer/runtime-definition'
import { useProcess, useSelect } from '~/store'

/** The contract shell owns one viewer; every contract surface reads and drives that one. */
export function useContractViewer() {
  const contractSigning = useProcess('contractSigning')
  const viewerState = useSelect('contractSigning', (state) => state.viewer)
  const sendViewerMessage = useMemo(
    () => (message: DocumentViewerMessage) =>
      contractSigning.send({ type: 'viewer', message: { type: 'viewer', message } }),
    [contractSigning]
  )
  return { viewerState, sendViewerMessage }
}

export function ContractDocumentViewport({
  document,
  renderPage
}: {
  document: { name: string }
  renderPage?: (page: STDocViewerRuntimeState['pages'][number]) => ReactNode
}) {
  const { viewerState, sendViewerMessage } = useContractViewer()
  return (
    <DocumentViewport
      send={sendViewerMessage}
      status={viewerState.pagesStatus}
      showPages={viewerState.viewer.viewportMeasurement != null}
      pagesToRender={viewerState.viewportState.pagesToRender}
      pageTransform={selViewerPageTransform(viewerState.viewer)}
      ariaLabel={`Viewing document ${document.name}`}
      loadingStatusLabel={`Loading document ${document.name}`}
    >
      {viewerState.pages.map((page) => (
        <Page
          key={page.pageNumber}
          page={page}
          topDivider={match(page.pageNumber)
            .with(1, () => null)
            .otherwise(() => undefined)}
          bottomDivider={null}
        >
          <Layer>
            <PDFPageSurface send={sendViewerMessage} pageStructure={page} />
          </Layer>
          {renderPage?.(page)}
        </Page>
      ))}
    </DocumentViewport>
  )
}
